import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Spinner,
  Alert,
  Form,
  InputGroup,
  ButtonGroup,
} from "react-bootstrap";
import ReactPaginate from "react-paginate";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { authFetch } from "../utils/authFetch";

const LUNI = [
  { val: 1, label: "01 - Ianuarie" },
  { val: 2, label: "02 - Februarie" },
  { val: 3, label: "03 - Martie" },
  { val: 4, label: "04 - Aprilie" },
  { val: 5, label: "05 - Mai" },
  { val: 6, label: "06 - Iunie" },
  { val: 7, label: "07 - Iulie" },
  { val: 8, label: "08 - August" },
  { val: 9, label: "09 - Septembrie" },
  { val: 10, label: "10 - Octombrie" },
  { val: 11, label: "11 - Noiembrie" },
  { val: 12, label: "12 - Decembrie" },
];

const toYMD = (d) => format(d, "yyyy-MM-dd");

function formatDateRo(val) {
  if (!val) return "";
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

export default function NcList({ simpleMode = true }) {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("articole"); // 'articole' sau 'jurnal'

  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Filters
  const [searchDocNo, setSearchDocNo] = useState("");
  const [searchExplic, setSearchExplic] = useState("");

  const [periodMode, setPeriodMode] = useState("month");
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const [startDate, setStartDate] = useState(new Date(currentYear, currentMonth - 1, 1));
  const [endDate, setEndDate] = useState(new Date(currentYear, currentMonth, 0));

  const [ncTypes, setNcTypes] = useState([]);
  const [selectedRecType, setSelectedRecType] = useState("");
  const [searchDebit, setSearchDebit] = useState("");
  const [searchCredit, setSearchCredit] = useState("");

  useEffect(() => {
    if (periodMode === "month") {
      const f = new Date(selectedYear, selectedMonth - 1, 1);
      const t = new Date(selectedYear, selectedMonth, 0);
      setStartDate(f);
      setEndDate(t);
    }
  }, [periodMode, selectedMonth, selectedYear]);

  // Load Note Types
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const typesData = await authFetch("/nc_type_cfg/");
        const rows = Array.isArray(typesData) ? typesData : (typesData?.items ?? []);
        setNcTypes(rows);
      } catch (err) {
        console.error("Error loading note types in List:", err);
      }
    };
    fetchTypes();
  }, []);

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      const skip = currentPage * itemsPerPage;

      const usp = new URLSearchParams();
      usp.set("skip", skip);
      usp.set("limit", itemsPerPage);
      usp.set("view_mode", viewMode);
      if (simpleMode) usp.set("manual_only", "true");

      if (searchDocNo?.trim()) usp.set("doc_no", searchDocNo.trim());
      if (searchExplic?.trim()) usp.set("explic", searchExplic.trim());
      if (startDate instanceof Date) usp.set("date_from", toYMD(startDate));
      if (endDate instanceof Date) usp.set("date_to", toYMD(endDate));
      if (selectedRecType) usp.set("rec_type", selectedRecType);
      if (searchDebit?.trim()) usp.set("debit_account", searchDebit.trim());
      if (searchCredit?.trim()) usp.set("credit_account", searchCredit.trim());

      const data = await authFetch(`/nc/list?${usp.toString()}`);
      if (data && data.items) {
        setItems(data.items);
        setTotalCount(data.total);
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [
    simpleMode,
    viewMode,
    currentPage,
    itemsPerPage,
    searchDocNo,
    searchExplic,
    startDate,
    endDate,
    selectedRecType,
    searchDebit,
    searchCredit,
  ]);

  const pageCount = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  // O mapare utilă pentru denumirea sursei (rec_type)
  const getRecTypeLabel = (type) => {
    const t = Number(type);
    const dynamicType = ncTypes.find((x) => Number(x.rec_type) === t);
    if (dynamicType) return dynamicType.name.toUpperCase();

    if (t === 0) return "INTRARE";
    if (t === 1) return "NIR";
    if (t === 2) return "PROCES VERBAL";
    if (t === 3) return "BON CONSUM";
    if (t === 4) return "BON TRANSFER";
    if (t === 5) return "IESIRE";
    if (t === 20) return "ÎNCASARE";
    if (t === 21) return "PLATĂ";
    return `Tip ${type}`;
  };

  const handleInchidere = async (tip) => {
    if (!window.confirm(`Sunteți sigur că doriți să generați notele de închidere ${tip} pentru luna ${selectedMonth}/${selectedYear}?`)) return;
    try {
      setLoading(true);
      await authFetch("/nc/inchidere-luna", {
        method: "POST",
        body: JSON.stringify({
          year: selectedYear,
          month: selectedMonth,
          tip: tip
        })
      });
      alert(`Închiderea ${tip} s-a realizat cu succes.`);
      await fetchList();
    } catch (e) {
      alert(`Eroare la închiderea ${tip}: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-3">
      <Row className="align-items-center mb-3">
        <Col className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h3 className="mb-0">{simpleMode ? "Note Simple" : "Lista Note Contabile (Jurnal)"}</h3>
          <div className="d-flex gap-2 align-items-center">
            {simpleMode && (
              <Button as={Link} to="/nc/new" variant="success" size="sm">
                + Notă Nouă
              </Button>
            )}
            <ButtonGroup size="sm">
              <Button
                variant={viewMode === "articole" ? "primary" : "outline-primary"}
                onClick={() => setViewMode("articole")}
              >
                Articole Contabile (D - C - Sumă)
              </Button>
              <Button
                variant={viewMode === "jurnal" ? "primary" : "outline-primary"}
                onClick={() => setViewMode("jurnal")}
              >
                Registru Jurnal
              </Button>
            </ButtonGroup>
          </div>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col md={2}>
          <Form.Control
            type="text"
            placeholder="Filtru nr. doc"
            value={searchDocNo}
            onChange={(e) => setSearchDocNo(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Control
            type="text"
            placeholder="Filtru explicație"
            value={searchExplic}
            onChange={(e) => setSearchExplic(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Control
            type="text"
            placeholder="Filtru cont debit"
            value={searchDebit}
            onChange={(e) => setSearchDebit(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Control
            type="text"
            placeholder="Filtru cont credit"
            value={searchCredit}
            onChange={(e) => setSearchCredit(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Select
            value={selectedRecType}
            onChange={(e) => setSelectedRecType(e.target.value)}
          >
            <option value="">Toate tipurile</option>
            {ncTypes.map((t) => (
              <option key={t.id} value={t.rec_type}>
                {t.name} ({t.rec_type})
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col
          md={2}
          className="d-flex justify-content-end align-items-center gap-2"
        >
          <Form.Select
            size="sm"
            style={{ width: 90 }}
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            title="Rânduri pe pagină"
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}/pag.
              </option>
            ))}
          </Form.Select>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setSearchDocNo("");
              setSearchExplic("");
              setPeriodMode("month");
              setSelectedMonth(currentMonth);
              setSelectedYear(currentYear);
              setSelectedRecType("");
              setSearchDebit("");
              setSearchCredit("");
            }}
          >
            Reset
          </Button>
        </Col>
      </Row>

      <Row className="mb-3 g-2 align-items-end">
        <Col md={2}>
          <Form.Label className="small mb-1 text-secondary">Mod Selectare Dată</Form.Label>
          <Form.Select
            size="sm"
            value={periodMode}
            onChange={(e) => setPeriodMode(e.target.value)}
          >
            <option value="month">După Lună</option>
            <option value="range">Interval de date</option>
          </Form.Select>
        </Col>

        {periodMode === "month" ? (
          <>
            <Col md={2}>
              <Form.Label className="small mb-1 text-secondary">Luna</Form.Label>
              <Form.Select
                size="sm"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                {LUNI.map((l) => (
                  <option key={l.val} value={l.val}>
                    {l.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={1}>
              <Form.Label className="small mb-1 text-secondary">Anul</Form.Label>
              <Form.Select
                size="sm"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
            </Col>
            {!simpleMode && (
              <Col md={7} className="d-flex gap-2">
                <Button size="sm" variant="outline-danger" onClick={() => handleInchidere("600")}>Închidere 600</Button>
                <Button size="sm" variant="outline-danger" onClick={() => handleInchidere("700")}>Închidere 700</Button>
                <Button size="sm" variant="outline-danger" onClick={() => handleInchidere("121")}>Închidere 121</Button>
              </Col>
            )}
          </>
        ) : (
          <>
            <Col md={2}>
              <Form.Label className="small mb-1 text-secondary">De la</Form.Label>
              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                dateFormat="dd/MM/yyyy"
                wrapperClassName="w-100"
                className="form-control form-control-sm"
                placeholderText="Start"
                isClearable
                selectsStart
                startDate={startDate}
                endDate={endDate}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1 text-secondary">Până la</Form.Label>
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                dateFormat="dd/MM/yyyy"
                wrapperClassName="w-100"
                className="form-control form-control-sm"
                placeholderText="End"
                isClearable
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate || undefined}
              />
            </Col>
          </>
        )}
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="py-5 text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive size="sm">
            <thead>
              {viewMode === "articole" ? (
                <tr>
                  <th>Sursă</th>
                  <th>Doc ID</th>
                  <th>Nr. Doc</th>
                  <th>Data NC</th>
                  <th>Cont Debit</th>
                  <th>Cont Credit</th>
                  <th>Explicație</th>
                  <th className="text-end">Sumă</th>
                  <th className="text-end">Acțiuni</th>
                </tr>
              ) : (
                <tr>
                  <th>Sursă</th>
                  <th>Doc ID</th>
                  <th>Nr. Doc</th>
                  <th>Data NC</th>
                  <th>Cont</th>
                  <th>Explicație</th>
                  <th className="text-end">Debit</th>
                  <th className="text-end">Credit</th>
                  <th>Cont Coresp.</th>
                  <th className="text-end">Acțiuni</th>
                </tr>
              )}
            </thead>
            <tbody>
              {items.map((it, idx) => {
                const isManualNote =
                  ncTypes.some((t) => Number(t.rec_type) === Number(it.rec_type)) ||
                  Number(it.rec_type) >= 100;

                return (
                  <tr key={`${it.rec_type}-${it.doc_id}-${it.id || idx}`}>
                    <td>{getRecTypeLabel(it.rec_type)}</td>
                    <td>{it.doc_id}</td>
                    <td>{it.doc_no || "-"}</td>
                    <td>{formatDateRo(it.data_nc) || "-"}</td>
                    
                    {viewMode === "articole" ? (
                      <>
                        <td className="fw-bold text-primary">{it.account}</td>
                        <td className="fw-bold text-danger">{it.accountcoresp}</td>
                        <td>{it.bxexplic || ""}</td>
                        <td className="text-end font-monospace fw-bold">
                          {Number(it.debit || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="fw-bold text-primary">{it.account}</td>
                        <td>{it.bxexplic || ""}</td>
                        <td className="text-end fw-bold">
                          {Number(it.debit || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                        <td className="text-end fw-bold">
                          {Number(it.credit || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                        <td className="text-muted">{it.accountcoresp || ""}</td>
                      </>
                    )}
                    
                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-1">
                        {(simpleMode || isManualNote) && (
                          <Button
                            as={Link}
                            to={`/nc/edit/${it.rec_type}/${it.doc_id}`}
                            size="sm"
                            variant="outline-warning"
                          >
                            Editează
                          </Button>
                        )}
                        <Button
                          as={Link}
                          to={`/nc/view/${it.rec_type}/${it.doc_id}`}
                          size="sm"
                          variant="outline-primary"
                        >
                          Vezi Nota
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={viewMode === "articole" ? 9 : 10} className="text-center text-muted py-4">
                    Nu există note contabile.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <ReactPaginate
            previousLabel={"← Înapoi"}
            nextLabel={"Înainte →"}
            pageCount={pageCount}
            forcePage={currentPage}
            onPageChange={(event) => setCurrentPage(event.selected)}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            containerClassName={"pagination justify-content-center"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            activeClassName={"active"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
          />
        </>
      )}
    </Container>
  );
}

